/// Draws each airport's runways as elongated white slabs with FAA-style
/// dashed centerlines. Geometry is computed in world-space pixels using
/// the projector; orientation comes from the magnetic heading recorded
/// in the level JSON (so the slab matches what the radar shows).
///
/// Centerline math is the same 120 ft / 80 ft (200 ft cycle) cadence
/// used by the iOS ground tower (FlatoutATC/Support/GroundTower3DController.swift:377).
///
/// Each airport also gets its name above and right of the field, with a
/// high field's elevation on a dimmer line under it, as on iOS.

import { Container, Graphics, Text, TextStyle } from 'pixi.js'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type { Airport, Helipad, Runway } from '../types/LevelData'
import { fieldElevationText, mapLabelScale } from './mapLabels'
import { radarTheme } from './theme'

const FT_PER_NM = 6076.115
const STRIPE_LEN_FT = 120
const STRIPE_CYCLE_FT = 200
const STRIPE_WIDTH_FT = 3

/// The airport name block, in world px at label scale 1. Proportions follow
/// iOS GameScene (name 24 pt bold, elevation 16 pt, name centered 40 pt right
/// of and 40 pt above the field, elevation 22 pt under the name), scaled to
/// the web's 14 px name.
const NAME_FONT_PX = 14
const ELEVATION_FONT_PX = 10
const NAME_OFFSET_PX = 23
const ELEVATION_GAP_PX = 13

export class RunwayLayer extends Container {
  /// One name block per airport, positioned at the field's center so a
  /// counter-scale keeps the name's offset from the field in proportion.
  private readonly nameBlocks: Container[] = []

  constructor(airports: Airport[], projector: CoordinateProjector) {
    super()
    // Names draw over every airport's runways, so a neighbor's runway
    // never covers them.
    const names = new Container()
    for (const airport of airports) {
      // Moving airports (carriers) render via CarrierLayer instead —
      // their geometry tracks the motion delta each tick and the
      // hull / angled deck visuals belong over there. Skipping here
      // prevents a stale static slab appearing under the live one.
      if (airport.motion) continue
      const airportNode = new Container()
      // Airport center marker
      const marker = new Graphics()
      const center = projector.project(airport.centerCoordinate, true)
      marker.circle(center.x, center.y, 4)
      marker.fill({ color: radarTheme.airportMarker, alpha: 1 })
      airportNode.addChild(marker)

      for (const runway of airport.runways) {
        airportNode.addChild(buildRunway(runway, projector))
        airportNode.addChild(buildRunwayNumberLabels(runway, projector))
      }
      for (const pad of airport.helipads ?? []) {
        airportNode.addChild(buildHelipad(pad, projector))
      }
      this.addChild(airportNode)

      const nameBlock = buildAirportNameBlock(airport, center)
      names.addChild(nameBlock)
      this.nameBlocks.push(nameBlock)
    }
    this.addChild(names)
  }

  /// Keeps the airport names at their authored size on screen when the
  /// player zooms out (iOS BACKLOG #133). `RadarScene` calls this on every
  /// `zoomed` event and once after its initial `setZoom`, which emits none.
  updateLabelScale(viewportScale: number): void {
    const scale = mapLabelScale(viewportScale)
    for (const block of this.nameBlocks) block.scale.set(scale)
  }
}

/// The airport's name, bold white, above and right of the field, and a
/// high field's elevation on a smaller, dimmer line under it. Ported from
/// the iOS airport label in GameScene setupWorld, which the web had not
/// carried over: on a map with several airports nothing said which field
/// was which.
function buildAirportNameBlock(airport: Airport, center: { x: number; y: number }): Container {
  const block = new Container()
  block.position.set(center.x, center.y)

  const name = new Text({
    text: airport.name,
    style: new TextStyle({
      fontFamily: 'Helvetica, Arial, sans-serif',
      fontSize: NAME_FONT_PX,
      fontWeight: 'bold',
      fill: 0xffffff,
    }),
  })
  // Bottom center at the offset point: iOS centers its label horizontally
  // on a baseline 40 pt above the field (y is down here, so above is minus).
  name.anchor.set(0.5, 1)
  name.alpha = 0.95
  name.resolution = 2
  name.position.set(NAME_OFFSET_PX, -NAME_OFFSET_PX)
  block.addChild(name)

  const elevation = fieldElevationText(airport.elevation)
  if (elevation) {
    const line = new Text({
      text: elevation,
      style: new TextStyle({
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: ELEVATION_FONT_PX,
        fill: 0xffffff,
      }),
    })
    line.anchor.set(0.5, 1)
    line.alpha = 0.7
    line.resolution = 2
    line.position.set(NAME_OFFSET_PX, -NAME_OFFSET_PX + ELEVATION_GAP_PX)
    block.addChild(line)
  }
  return block
}

function buildRunway(runway: Runway, projector: CoordinateProjector): Container {
  const node = new Container()

  const a = projector.project(runway.startCoordinate, true)
  const b = projector.project(runway.endCoordinate, true)
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lengthPx = Math.hypot(dx, dy)
  if (lengthPx < 1) return node

  const angleRad = Math.atan2(dy, dx)
  const midX = (a.x + b.x) / 2
  const midY = (a.y + b.y) / 2

  // Width: real runways are ~150 ft; render 3x for visibility on radar.
  const widthPx = (450 / FT_PER_NM) * pixelsPerNM(projector)

  const slab = new Graphics()
  slab.rect(-lengthPx / 2, -widthPx / 2, lengthPx, widthPx)
  slab.fill({ color: radarTheme.runway, alpha: 0.95 })

  const slabContainer = new Container()
  slabContainer.position.set(midX, midY)
  slabContainer.rotation = angleRad
  slabContainer.addChild(slab)

  // Dashed centerline — stripes laid down from the midpoint outward
  // until they would extend past the threshold. Mirrors iOS spec.
  const stripeLenPx = (STRIPE_LEN_FT / FT_PER_NM) * pixelsPerNM(projector)
  const cyclePx = (STRIPE_CYCLE_FT / FT_PER_NM) * pixelsPerNM(projector)
  const stripeWidthPx = Math.max(1, (STRIPE_WIDTH_FT / FT_PER_NM) * pixelsPerNM(projector))
  const halfLen = lengthPx / 2

  const stripes = new Graphics()
  let i = 0
  while (true) {
    const centerOffset = i * cyclePx
    if (centerOffset + stripeLenPx / 2 > halfLen) break
    const signs = i === 0 ? [0] : [1, -1]
    for (const sign of signs) {
      const cx = sign * centerOffset
      stripes.rect(cx - stripeLenPx / 2, -stripeWidthPx / 2, stripeLenPx, stripeWidthPx)
    }
    i += 1
  }
  stripes.fill({ color: radarTheme.centerline, alpha: 1 })
  slabContainer.addChild(stripes)

  return slabContainer
}

/// The projector's effective pixels-per-NM scale, recovered from a
/// 1°-of-longitude probe at the world center. Cheap; exact-enough.
function pixelsPerNM(projector: CoordinateProjector): number {
  const a = projector.project({ latitude: 0, longitude: 0 }, false)
  const b = projector.project({ latitude: 0, longitude: 1 / 60 }, false)
  return Math.abs(b.x - a.x) // 1' of arc at the equator ≈ 1 NM
}

/// Two small text labels per runway — the heading-derived runway IDs
/// (e.g. "04" / "22"), positioned outward beyond each threshold and
/// offset perpendicular so they don't overlap the slab.
///
/// Mirrors the iOS layout from GameScene.swift:1593-1648 — same
/// outward + perpendicular offsets, same "always offset to the right
/// of start→end" placement so both labels sit on the same side of
/// the runway.
function buildRunwayNumberLabels(
  runway: Runway,
  projector: CoordinateProjector,
): Container {
  const node = new Container()
  const a = projector.project(runway.startCoordinate, true)
  const b = projector.project(runway.endCoordinate, true)
  const dx = b.x - a.x
  const dy = b.y - a.y
  const length = Math.hypot(dx, dy)
  if (length < 1) return node
  // Unit vector along start→end and its right-perpendicular.
  const ux = dx / length
  const uy = dy / length
  const px = uy
  const py = -ux
  const outward = 8
  const perp = 7
  const style = new TextStyle({
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontSize: 8,
    fontWeight: '300',
    fill: 0xffffff,
  })
  const id1 = new Text({ text: runway.id1, style })
  id1.anchor.set(0.5)
  id1.alpha = 0.65
  // resolution bump keeps the small text crisp under zoom-in (Pixi
  // re-rasterises Text at this multiple of the base font size).
  id1.resolution = 2
  id1.position.set(a.x - ux * outward + px * perp, a.y - uy * outward + py * perp)
  const id2 = new Text({ text: runway.id2, style })
  id2.anchor.set(0.5)
  id2.alpha = 0.65
  id2.resolution = 2
  id2.position.set(b.x + ux * outward + px * perp, b.y + uy * outward + py * perp)
  node.addChild(id1)
  node.addChild(id2)
  return node
}

/// Helipad marker — a 6 px circle with a centered "H" letter,
/// rendered in the same airport-marker palette as the ARP dot
/// so the eye associates them. Optional id label below the
/// circle when provided.
function buildHelipad(pad: Helipad, projector: CoordinateProjector): Container {
  const node = new Container()
  const center = projector.project(pad.coordinate, true)
  const ring = new Graphics()
  ring.circle(center.x, center.y, 6)
  ring.stroke({ width: 1.5, color: radarTheme.airportMarker, alpha: 0.95 })
  ring.fill({ color: 0x000000, alpha: 0.55 })
  node.addChild(ring)

  const hStyle = new TextStyle({
    fill: radarTheme.airportMarker,
    fontSize: 8,
    fontFamily: 'monospace',
    fontWeight: 'bold',
  })
  const h = new Text({ text: 'H', style: hStyle })
  h.anchor.set(0.5)
  h.resolution = 2
  h.position.set(center.x, center.y)
  node.addChild(h)

  if (pad.id) {
    const idStyle = new TextStyle({
      fill: radarTheme.airportMarker,
      fontSize: 7,
      fontFamily: 'monospace',
    })
    const idLabel = new Text({ text: pad.id, style: idStyle })
    idLabel.anchor.set(0.5, 0)
    idLabel.alpha = 0.7
    idLabel.resolution = 2
    idLabel.position.set(center.x, center.y + 8)
    node.addChild(idLabel)
  }
  return node
}
